import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthBrand from "../../core/components/AuthBrand";
import { getBusinessWorkflows } from "../businessModeService";
import {
  buildActionItemsListPath,
  clearBusinessOnboardingConnectSetup,
  clearPasteTranscriptHintDismissed,
  consumePostSetupWorkflowFocus,
  persistBusinessOnboardingConnectSetup,
  persistManualActionItemsModePreferred,
} from "./actionItemHelpers";
import ConnectSourcesEmptyState from "./shared/ConnectSourcesEmptyState";
import ConnectorRequestModal from "./shared/ConnectorRequestModal";
import { CONNECT_SOURCES_ONBOARDING_LIVE } from "./shared/connectSourcesCatalog";
import "../../core/styles/authSurface.css";
import "./BusinessMode.css";

const BusinessOnboardingPage = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(true);
  const [connectorRequestTarget, setConnectorRequestTarget] = useState(null);

  const connectedWorkflowTypes = workflows.map((w) =>
    String(w.workflow_type || "").trim()
  );
  const anyConnected = connectedWorkflowTypes.length > 0;

  const loadWorkflows = useCallback(async () => {
    try {
      const data = await getBusinessWorkflows();
      setWorkflows(Array.isArray(data) ? data : []);
    } catch {
      setWorkflows([]);
    } finally {
      setWorkflowsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const handleConnectApp = (app) => {
    persistBusinessOnboardingConnectSetup();
    navigate(app.setupPath, { state: { from: "onboarding" } });
  };

  const handleContinue = () => {
    clearBusinessOnboardingConnectSetup();
    consumePostSetupWorkflowFocus();
    navigate(buildActionItemsListPath({ firstPull: true }));
  };

  const handlePasteTranscriptInstead = useCallback(() => {
    clearBusinessOnboardingConnectSetup();
    persistManualActionItemsModePreferred();
    clearPasteTranscriptHintDismissed();
    navigate(buildActionItemsListPath({ pasteTranscript: true }));
  }, [navigate]);

  return (
    <div className="biz-onboard">
      <div className="biz-onboard__header">
        <AuthBrand className="task-mantis-auth-brand--compact biz-onboard__brand" />
      </div>
      <div className="biz-onboard__body">
        {workflowsLoading ? (
          <div className="biz-onboard__loading">Loading…</div>
        ) : (
          <ConnectSourcesEmptyState
            layout="onboarding"
            liveApps={CONNECT_SOURCES_ONBOARDING_LIVE}
            headline="Where do you record your meetings?"
            showPreviewHint={false}
            showTrustCard={false}
            showComingSoon={true}
            collapseComingSoon={false}
            connectedWorkflowTypes={connectedWorkflowTypes}
            onConnectApp={handleConnectApp}
            onRequestSource={setConnectorRequestTarget}
            footerCta={{
              label: anyConnected ? "See my action items →" : "Connect a source to continue",
              disabled: !anyConnected,
              onClick: handleContinue,
            }}
            escapeAction={{
              label: "Not ready? Add tasks manually instead",
              onClick: handlePasteTranscriptInstead,
            }}
          />
        )}
      </div>
      <ConnectorRequestModal
        connector={connectorRequestTarget}
        source="onboarding"
        onClose={() => setConnectorRequestTarget(null)}
      />
    </div>
  );
};

export default BusinessOnboardingPage;
